/**
 * Header plaque for the differences-overview modal: the two compared IFC files
 * as OLDER / NEWER chips with the swap control between them. Each chip is a
 * picker — clicking anywhere on the box opens a searchable dropdown of the
 * building's processed .ifc files, so users can re-target either side of the
 * diff. Multi-version files list their latest revision at top level and expand
 * to show superseded versions indented underneath.
 */

import { useMemo, useState } from "react";
import { ArrowLeftRight, ChevronDown, ChevronRight } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { FileTypeIcon } from "@/components/ui/documents/file-type-icon";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { BuildingIfcViewerSections } from "@/lib/i18n/types/buildings-sections/ifc-viewer";
import {
  candidateBadgeVersion,
  groupCandidatesIntoChains,
  type IfcModelCandidate,
} from "@/domains/buildings/hooks/useIfcModelCandidates";

export interface IfcFileChip {
  filename: string;
  /** Shown as a "v{n}" badge. Null for single-version files so the badge only
   * appears where it disambiguates. */
  version: number | null;
}

interface IfcDiffVersionPlaqueProps {
  older: IfcFileChip;
  newer: IfcFileChip;
  onSwap: () => void;
  tv: BuildingIfcViewerSections;
  /** Files the user can re-target either side to. Empty while loading — the
   * chip then isn't a picker. */
  candidates: IfcModelCandidate[];
  versionGroupCounts: Map<string, number>;
  /** Currently-compared doc ids, so each picker can disable the file already in
   * the opposite slot. */
  olderDocId: number;
  newerDocId: number;
  onPickOlder: (docId: number) => void;
  onPickNewer: (docId: number) => void;
  /** Optional override for each chip's trigger box — the on-canvas viewer passes
   * its dark-glass chrome. Defaults to the light-modal look. */
  boxClassName?: string;
  /** Optional override for each picker's dropdown surface. The viewer passes
   * `GLASS_PANEL` (which carries the `dark` class) so the portaled dropdown —
   * which can't inherit a `dark` ancestor — renders on dark chrome too. */
  popoverClassName?: string;
  /** When supplied, each side splits into a role toggle (sets which model the 3D
   * scene renders) beside the re-target trigger. Omitted in the modal, where
   * there is no active-render concept and the whole chip is the trigger. */
  activeSide?: "older" | "newer";
  onSetActiveSide?: (side: "older" | "newer") => void;
}

/** White pill with a border — reads as a tag, not a status colour. */
const VERSION_BADGE =
  "shrink-0 border-border bg-background px-1.5 py-0 text-[10px] text-foreground";

function VersionBadge({ version }: { version: number }) {
  return (
    <Badge variant="outline" className={VERSION_BADGE}>
      v{version}
    </Badge>
  );
}

/** One selectable file row. Older revisions render indented under their head. */
function FileRow({
  cand,
  version,
  disabled,
  indented,
  onPick,
  children,
}: {
  cand: IfcModelCandidate;
  version: number | null;
  disabled: boolean;
  indented?: boolean;
  onPick: (docId: number) => void;
  /** Expander slot, rendered before the icon on multi-version heads. */
  children?: React.ReactNode;
}) {
  return (
    <CommandItem
      // cmdk filters on this string — match on the filename and the version.
      value={`${cand.filename} ${cand.version != null ? `v${cand.version}` : ""} ${cand.docId}`}
      disabled={disabled}
      onSelect={() => onPick(cand.docId)}
      className={cn("flex items-center gap-2", indented && "pl-8")}
    >
      {children}
      <FileTypeIcon filename={cand.filename} className="h-5 w-5" />
      <span className="min-w-0 flex-1 truncate text-sm" title={cand.filename}>
        {cand.filename}
      </span>
      {version != null && <VersionBadge version={version} />}
    </CommandItem>
  );
}

function FilePicker({
  chip,
  candidates,
  versionGroupCounts,
  disabledDocId,
  onPick,
  roleLabel,
  animation,
  tv,
  boxClassName,
  popoverClassName,
  isActive,
  onSetActive,
}: {
  chip: IfcFileChip;
  candidates: IfcModelCandidate[];
  versionGroupCounts: Map<string, number>;
  disabledDocId: number;
  onPick: (docId: number) => void;
  roleLabel: string;
  animation: string;
  tv: BuildingIfcViewerSections;
  boxClassName?: string;
  popoverClassName?: string;
  /** Split mode only: is this the side the 3D scene currently renders? */
  isActive?: boolean;
  /** Split mode only: make this side the rendered one. Its presence switches the
   * chip from a single trigger to a role-toggle + re-target trigger pair. */
  onSetActive?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const chains = useMemo(() => groupCandidatesIntoChains(candidates), [candidates]);
  const pickable = candidates.length > 0;
  // `onSetActive` present ⇒ the on-canvas viewer wants the role toggle beside the
  // re-target trigger; absent ⇒ the modal, where the whole chip is one trigger.
  const split = onSetActive != null;

  // While searching, reveal every revision — an older version the user typed the
  // name of must be findable without hunting for the chain that hides it.
  const searching = search.trim().length > 0;
  const isExpanded = (key: string) => searching || expanded.has(key);

  const toggle = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const pick = (docId: number) => {
    onPick(docId);
    setOpen(false);
  };

  // Modal chip: file icon + stacked role/filename. Split chip omits the role
  // label (it moves to the toggle button) and lays the file info out inline.
  const box = split ? (
    <>
      <FileTypeIcon filename={chip.filename} className="h-5 w-5 shrink-0" />
      <span className="min-w-0 truncate text-xs font-medium" title={chip.filename}>
        {chip.filename}
      </span>
      {chip.version != null && <VersionBadge version={chip.version} />}
    </>
  ) : (
    <>
      <FileTypeIcon filename={chip.filename} className="h-6 w-6" />
      <div className="flex min-w-0 flex-col text-left">
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          {roleLabel}
        </span>
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="min-w-0 truncate text-xs font-medium" title={chip.filename}>
            {chip.filename}
          </span>
          {chip.version != null && <VersionBadge version={chip.version} />}
        </span>
      </div>
    </>
  );

  const boxClasses = cn(
    split
      ? "flex min-w-0 items-center gap-1.5 rounded-full px-2 py-1 text-muted-foreground hover:bg-muted/50 hover:text-foreground"
      : "flex min-w-0 items-center gap-2 rounded-lg border border-border bg-background px-3 py-1.5",
    boxClassName
  );

  // The role toggle that sits left of the re-target trigger in split mode. Its
  // active-ring styling matches the old CompareModelChip so the rendered side
  // stays obvious.
  const roleToggle = split ? (
    <button
      type="button"
      onClick={onSetActive}
      aria-pressed={isActive}
      title={roleLabel}
      className={cn(
        "flex shrink-0 items-center rounded-full px-2 py-1 text-[9px] uppercase tracking-wide transition-colors",
        isActive
          ? "bg-muted font-medium text-foreground ring-1 ring-border"
          : "text-muted-foreground hover:bg-muted/50"
      )}
    >
      {roleLabel}
    </button>
  ) : null;

  // The re-target trigger (or a static box when there are no candidates yet).
  const trigger = !pickable ? (
    <div className={cn(boxClasses, animation)}>{box}</div>
  ) : (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={tv.diffChangeFile}
          title={tv.diffChangeFile}
          className={cn(
            boxClasses,
            animation,
            "origin-center transition-transform duration-150 hover:scale-[1.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          )}
        >
          {box}
        </button>
      </PopoverTrigger>
      <PopoverContent className={cn("w-80 p-0", popoverClassName)} align="start">
        <Command>
          <CommandInput placeholder={tv.diffSearchFiles} value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>{tv.diffNoFiles}</CommandEmpty>
            <CommandGroup>
              {chains.map((chain) => {
                const key = chain.head.versionGroup ?? `doc:${chain.head.docId}`;
                const hasOlder = chain.older.length > 0;
                const showOlder = hasOlder && isExpanded(key);
                return (
                  <div key={key}>
                    <FileRow
                      cand={chain.head}
                      version={candidateBadgeVersion(chain.head, versionGroupCounts)}
                      disabled={chain.head.docId === disabledDocId}
                      onPick={pick}
                    >
                      {hasOlder ? (
                        <span
                          role="button"
                          tabIndex={0}
                          aria-expanded={showOlder}
                          aria-label={tv.diffOlderVersions}
                          title={tv.diffOlderVersions}
                          className="-ml-1 flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          // cmdk selects rows via the bubbling click, not pointerdown —
                          // both must be stopped or expanding also picks the head row.
                          onPointerDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggle(key);
                          }}
                          // Enter/Space toggle for keyboard users; stopPropagation so
                          // cmdk's root keydown doesn't also select the active row.
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.stopPropagation();
                              toggle(key);
                            }
                          }}
                        >
                          {showOlder ? (
                            <ChevronDown className="size-3.5" />
                          ) : (
                            <ChevronRight className="size-3.5" />
                          )}
                        </span>
                      ) : (
                        <span className="-ml-1 size-5 shrink-0" aria-hidden="true" />
                      )}
                    </FileRow>
                    {showOlder &&
                      chain.older.map((old) => (
                        <FileRow
                          key={old.docId}
                          cand={old}
                          version={candidateBadgeVersion(old, versionGroupCounts)}
                          disabled={old.docId === disabledDocId}
                          indented
                          onPick={pick}
                        />
                      ))}
                  </div>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );

  // Modal: the trigger is the whole chip. Split: role toggle + trigger share one
  // capsule (siblings — never nested, which would be invalid button-in-button).
  if (!split) return trigger;
  return (
    <div className="flex min-w-0 items-center gap-0.5">
      {roleToggle}
      {trigger}
    </div>
  );
}

export function IfcDiffVersionPlaque({
  older,
  newer,
  onSwap,
  tv,
  candidates,
  versionGroupCounts,
  olderDocId,
  newerDocId,
  onPickOlder,
  onPickNewer,
  boxClassName,
  popoverClassName,
  activeSide,
  onSetActiveSide,
}: IfcDiffVersionPlaqueProps) {
  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <FilePicker
        key={`older-${olderDocId}`}
        chip={older}
        roleLabel={tv.compareModelOlder}
        animation="animate-in fade-in slide-in-from-left-1 duration-200"
        candidates={candidates}
        versionGroupCounts={versionGroupCounts}
        disabledDocId={newerDocId}
        onPick={onPickOlder}
        tv={tv}
        boxClassName={boxClassName}
        popoverClassName={popoverClassName}
        isActive={activeSide === "older"}
        onSetActive={onSetActiveSide ? () => onSetActiveSide("older") : undefined}
      />
      <Button
        variant="ghost"
        size="icon"
        className="size-7 shrink-0 transition-transform duration-150 hover:scale-110"
        onClick={onSwap}
        aria-label={tv.compareSwap}
        title={tv.compareSwap}
      >
        <ArrowLeftRight className="size-4" />
      </Button>
      <FilePicker
        key={`newer-${newerDocId}`}
        chip={newer}
        roleLabel={tv.compareModelNewer}
        animation="animate-in fade-in slide-in-from-right-1 duration-200"
        candidates={candidates}
        versionGroupCounts={versionGroupCounts}
        disabledDocId={olderDocId}
        onPick={onPickNewer}
        tv={tv}
        boxClassName={boxClassName}
        popoverClassName={popoverClassName}
        isActive={activeSide === "newer"}
        onSetActive={onSetActiveSide ? () => onSetActiveSide("newer") : undefined}
      />
    </div>
  );
}
